import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from './ui/Modal'
import { getDataAPI, postDataAPI } from '../lib/api'

type TutorialTrack = 'artist' | 'manager' | 'support' | 'team-member'

type Step = {
  title: string
  description: string
  duration: string
  items?: Array<{
    title: string
    description: string
  }>
  showGoogleSheetsCredentials?: boolean
  actions?: Array<{
    label: string
    type: 'primary' | 'secondary'
    onClick: 'openGoogleCalendar' | 'openGoogleSheets' | 'openShows' | 'openMusic' | 'openTeam' | 'openSettings'
  }>
}

const stepsByRole: Record<TutorialTrack, Step[]> = {
  artist: [
    {
      title: 'Welcome to Dizee',
      description: 'Let\'s get you set up in a few quick steps.',
      duration: '7m',
      items: [
        {
          title: 'Centralize Everything',
          description: 'Manage releases, shows, schedule, and business operations in one intelligent workspace.',
        },
        {
          title: 'Sync Automatically',
          description: 'Connect Google Calendar and Sheets for real-time syncing across all your tools.',
        },
        {
          title: 'Collaborate Seamlessly',
          description: 'Work with your team through chat, shared tasks, and role-based permissions.',
        },
      ],
    },
    {
      title: 'Connect Google Calendar',
      description: 'Sync your shows, tasks, and events automatically.',
      duration: '1m',
      items: [
        {
          title: 'Two-Way Sync',
          description: 'Events you create in Dizee appear in Google Calendar, and vice versa—no manual updates.',
        },
        {
          title: 'Never Miss a Beat',
          description: 'Your shows, rehearsals, and tasks stay in sync across all devices automatically.',
        },
        {
          title: 'One-Click Setup',
          description: 'Click below to connect your Google account. It takes less than 30 seconds.',
        },
      ],
      actions: [
        { label: 'Connect Google Calendar', type: 'primary', onClick: 'openGoogleCalendar' },
      ],
    },
    {
      title: 'Set Up Google Sheets',
      description: 'Copy our template, paste your credentials below, and deploy the script.',
      duration: '3m',
      showGoogleSheetsCredentials: true,
      items: [
        {
          title: 'Copy & Configure',
          description: 'Click "Open Template", make a copy, then paste the credentials shown below into your sheet\'s Settings tab.',
        },
        {
          title: 'Deploy Script',
          description: 'In your sheet, go to Extensions → Apps Script → Deploy → New deployment. Set as "Web app", access "Anyone", then copy the URL back to Dizee Settings.',
        },
        {
          title: 'Start Syncing',
          description: 'Use Bookings and Catalog tabs—edits sync to Dizee instantly.',
        },
      ],
      actions: [
        { label: 'Open Google Sheets Template', type: 'primary', onClick: 'openGoogleSheets' },
      ],
    },
    {
      title: 'Import Your Shows',
      description: 'Upload existing show data to get started quickly.',
      duration: '2m',
      items: [
        {
          title: 'CSV Upload',
          description: 'Have a spreadsheet of shows? Export it as CSV and use the upload tool below to instantly import all your tour dates.',
        },
        {
          title: 'Drag and Drop',
          description: 'Drag your CSV file directly into the upload area or click to browse your files.',
        },
        {
          title: 'Quick Import',
          description: 'Once uploaded, review the parsed data and click "Import Shows" to add everything to Dizee.',
        },
      ],
      actions: [
        { label: 'Upload CSV', type: 'primary', onClick: 'openShows' },
      ],
    },
    {
      title: 'Invite Your Team',
      description: 'Collaborate with managers, support staff, and collaborators.',
      duration: '1m',
      items: [
        {
          title: 'Send Invitations',
          description: 'Invite team members by email and assign them as managers or support.',
        },
        {
          title: 'Set Permissions',
          description: 'Control who can view or edit Music, Shows, Finance, Tasks, Chat, and more.',
        },
        {
          title: 'Collaborate in Real-Time',
          description: 'Use Chat and Tasks to coordinate with your team. Mentions and notifications keep everyone aligned.',
        },
      ],
    },
    {
      title: 'You\'re All Set',
      description: 'Everything is ready. Here\'s what to explore next.',
      duration: '1m',
      items: [
        {
          title: 'Explore Your Dashboard',
          description: 'Overview shows upcoming events, recent activity, and priority items at a glance.',
        },
        {
          title: 'Track Your Finances',
          description: 'Finance tracks income from streaming, shows, and merch. Export reports for accounting or investors.',
        },
        {
          title: 'Stay Organized with Tasks',
          description: 'Create to-dos, assign them to team members, and track progress with due dates and priorities.',
        },
      ],
    },
  ],
  manager: [
    {
      title: 'Welcome to Dizee',
      description: 'Let\'s get you set up to manage multiple artists efficiently.',
      duration: '8m',
      items: [
        {
          title: 'Multi-Artist Management',
          description: 'Switch seamlessly between artists and manage their releases, shows, schedules, and operations.',
        },
        {
          title: 'Automated Syncing',
          description: 'Connect Google Calendar and Sheets to keep everything in sync across all artists.',
        },
        {
          title: 'Team Collaboration',
          description: 'Coordinate with tasks, chat, and role-based permissions across all accounts.',
        },
      ],
    },
    {
      title: 'Connect Google Calendar',
      description: 'Sync events for all your artists automatically.',
      duration: '1m',
      items: [
        {
          title: 'Centralized Calendar',
          description: 'All shows, tasks, and events for every artist you manage appear in one Google Calendar.',
        },
        {
          title: 'Bidirectional Sync',
          description: 'Create events in Dizee or Google Calendar—they sync automatically both ways.',
        },
        {
          title: 'Quick Setup',
          description: 'Connect your Google account below. This works across all artist accounts you manage.',
        },
      ],
      actions: [
        { label: 'Connect Google Calendar', type: 'primary', onClick: 'openGoogleCalendar' },
      ],
    },
    {
      title: 'Set Up Google Sheets',
      description: 'Copy our template, paste your credentials below, and deploy the script.',
      duration: '3m',
      showGoogleSheetsCredentials: true,
      items: [
        {
          title: 'Copy & Configure',
          description: 'Click "Open Template", make a copy, then paste the credentials shown below into your sheet\'s Settings tab.',
        },
        {
          title: 'Deploy Script',
          description: 'In your sheet, go to Extensions → Apps Script → Deploy → New deployment. Set as "Web app", access "Anyone", then copy the URL back to Dizee Settings.',
        },
        {
          title: 'Start Syncing',
          description: 'Use Bookings and Catalog tabs—edits sync to Dizee instantly.',
        },
      ],
      actions: [
        { label: 'Open Google Sheets Template', type: 'primary', onClick: 'openGoogleSheets' },
      ],
    },
    {
      title: 'Import Artist Shows',
      description: 'Upload existing tour data for your artists.',
      duration: '2m',
      items: [
        {
          title: 'CSV Bulk Import',
          description: 'Have a spreadsheet of shows? Export it as CSV and use the upload tool to instantly import all tour dates.',
        },
        {
          title: 'Drag and Drop',
          description: 'Drag your CSV file directly into the upload area or click to browse your files.',
        },
        {
          title: 'Quick Import',
          description: 'Once uploaded, review the parsed data and click "Import Shows" to add everything to Dizee.',
        },
      ],
      actions: [
        { label: 'Upload CSV', type: 'primary', onClick: 'openShows' },
      ],
    },
    {
      title: 'Build Artist Teams',
      description: 'Invite collaborators and set granular permissions.',
      duration: '1m',
      items: [
        {
          title: 'Invite Team Members',
          description: 'Add support staff, collaborators, and other managers to each artist account.',
        },
        {
          title: 'Granular Access Control',
          description: 'Set permissions per person—control who can view or edit Music, Shows, Finance, Tasks, Chat, and more.',
        },
        {
          title: 'Real-Time Coordination',
          description: 'Use Chat and Tasks to coordinate across teams. Mentions and notifications keep everyone aligned.',
        },
      ],
    },
    {
      title: 'You\'re Ready to Manage',
      description: 'Your multi-artist operation is ready to scale.',
      duration: '1m',
      items: [
        {
          title: 'Switch Between Artists',
          description: 'Use the Artist Switcher in the sidebar to instantly jump between accounts.',
        },
        {
          title: 'Aggregated Overview',
          description: 'Overview shows activity, upcoming events, and priorities across all your artists.',
        },
        {
          title: 'Financial Reporting',
          description: 'Finance tracks revenue per artist. Export detailed reports for accounting or stakeholders.',
        },
      ],
    },
  ],
  support: [
    {
      title: 'Welcome to Dizee',
      description: 'Let\'s get you set up to support your artists.',
      duration: '6m',
      items: [
        {
          title: 'Permission-Based Access',
          description: 'Access artist accounts to manage releases, shows, schedules, and operations within your permissions.',
        },
        {
          title: 'Syncing Tools',
          description: 'Use Google Calendar and Sheets integrations (if connected) to stay organized.',
        },
        {
          title: 'Team Collaboration',
          description: 'Coordinate with tasks, real-time chat, and team tools.',
        },
      ],
    },
    {
      title: 'Check Google Calendar',
      description: 'See if the artist has connected Google Calendar.',
      duration: '1m',
      items: [
        {
          title: 'Artist-Controlled',
          description: 'Google Calendar is configured by the artist. If connected, events sync automatically.',
        },
        {
          title: 'View Schedule',
          description: 'Use the Schedule page to view all shows, tasks, and events in a unified timeline.',
        },
        {
          title: 'Ask for Access',
          description: 'If you need calendar permissions, reach out to the artist or team lead.',
        },
      ],
    },
    {
      title: 'Understand Google Sheets',
      description: 'Learn how the artist uses Sheets (if connected).',
      duration: '1m',
      items: [
        {
          title: 'Artist-Managed Template',
          description: 'The artist may have set up a Google Sheet that syncs with Dizee. Ask them for access if needed.',
        },
        {
          title: 'Real-Time Updates',
          description: 'If Sheets is connected, edits in the sheet appear instantly in Dizee.',
        },
        {
          title: 'Bulk Operations',
          description: 'You can update many shows at once via the sheet (if you have edit permissions).',
        },
      ],
    },
    {
      title: 'Help Import Shows',
      description: 'Assist with uploading or managing tour data.',
      duration: '2m',
      items: [
        {
          title: 'CSV Upload',
          description: 'If you have access, upload CSV files to import shows for the artist using the upload tool.',
        },
        {
          title: 'Drag and Drop',
          description: 'Drag your CSV file directly into the upload area or click to browse your files.',
        },
        {
          title: 'Permission Awareness',
          description: 'Your access to Shows depends on permissions set by the artist.',
        },
      ],
      actions: [
        { label: 'Upload CSV', type: 'primary', onClick: 'openShows' },
      ],
    },
    {
      title: 'Understand Your Role',
      description: 'View your permissions and team access.',
      duration: '1m',
      items: [
        {
          title: 'Team Visibility',
          description: 'View other team members supporting the same artist in the Team page.',
        },
        {
          title: 'Your Access Levels',
          description: 'Your permissions determine which sections you can view or edit—check with the artist for changes.',
        },
        {
          title: 'Multi-Artist Support',
          description: 'If you support multiple artists, use the Artist Switcher in the sidebar to move between accounts.',
        },
      ],
    },
    {
      title: 'You\'re Ready to Support',
      description: 'Help artists succeed with Dizee.',
      duration: '1m',
      items: [
        {
          title: 'Explore Your Access',
          description: 'Navigate to sections you have permissions for. Data syncs automatically.',
        },
        {
          title: 'Use Tasks and Chat',
          description: 'Create tasks, assign them, and communicate with the team in real-time.',
        },
        {
          title: 'Request Permissions',
          description: 'Need access to Finance or other sections? Reach out to the artist or team lead.',
        },
      ],
    },
  ],
  'team-member': [
    {
      title: 'Welcome to Your Team Dashboard',
      description: 'Here\'s how Dizee works when you\'re part of an artist\'s team.',
      duration: '5m',
      items: [
        {
          title: 'You\'ve Been Invited',
          description: 'An artist has added you to their team. You\'ll see their data based on the permissions they\'ve granted you.',
        },
        {
          title: 'Permission-Based Access',
          description: 'Your sidebar only shows sections you have access to—Shows, Music, Tasks, and more depend on what the artist has enabled for you.',
        },
        {
          title: 'Everything Stays in Sync',
          description: 'Any changes you make (within your permissions) are reflected in real-time for the entire team.',
        },
      ],
    },
    {
      title: 'Managing Your Accounts',
      description: 'If you\'re on multiple teams, here\'s how to navigate between them.',
      duration: '1m',
      items: [
        {
          title: 'Account Switcher',
          description: 'Use the toggle switches in the sidebar to turn artist accounts on or off. This controls which data you see across all sections.',
        },
        {
          title: 'Consolidated View',
          description: 'When multiple accounts are on, most sections blend data from all active accounts into one view—so you can see everything at once.',
        },
        {
          title: 'Your Toggles Persist',
          description: 'The accounts you turn on or off will stay that way when you sign back in, so you pick up right where you left off.',
        },
      ],
      actions: [
        { label: 'Go to Team Hub', type: 'primary', onClick: 'openTeam' },
      ],
    },
    {
      title: 'Edits and Approvals',
      description: 'How your changes are tracked and reviewed.',
      duration: '1m',
      items: [
        {
          title: 'Activity Tracking',
          description: 'Every edit you make is logged in the Activity section. The artist and other team members with access can see what changed and when.',
        },
        {
          title: 'Approval Workflow',
          description: 'If you don\'t have approval authority, your edits will be held as "pending" until someone with authority approves or denies them.',
        },
        {
          title: 'Pending Items',
          description: 'While an edit is pending, the item shows an orange dot and can\'t be changed by anyone until it\'s resolved.',
        },
      ],
    },
    {
      title: 'Understanding Your Permissions',
      description: 'What you can and can\'t do depends on your role.',
      duration: '1m',
      items: [
        {
          title: 'View vs. Edit',
          description: 'Some sections may be view-only for you, while others allow full editing. Check with the artist if you need different access.',
        },
        {
          title: 'Team Management',
          description: 'If granted, you can invite or manage other team members on the artist\'s behalf from the Team page.',
        },
        {
          title: 'Financial Data',
          description: 'Access to financial information is controlled separately—you\'ll only see it if the artist has explicitly enabled it for you.',
        },
      ],
    },
    {
      title: 'You\'re All Set',
      description: 'You\'re ready to collaborate.',
      duration: '1m',
      items: [
        {
          title: 'Explore Your Sections',
          description: 'Navigate the sidebar to see what\'s available to you. Sections you don\'t have access to won\'t appear.',
        },
        {
          title: 'Use Tasks and Chat',
          description: 'Coordinate with the artist and other team members through shared tasks and real-time chat.',
        },
        {
          title: 'Check Activity',
          description: 'If you have activity access, use the Activity page to track changes, approvals, and the overall source of truth.',
        },
      ],
    },
  ],
}

const TEAM_MEMBER_ROLES = ['manager', 'support', 'promoter']

const getTrackFromStorage = (): { track: TutorialTrack; isTeamMember: boolean } | null => {
  const user = localStorage.getItem('dizeeUser')
  if (!user) return null
  try {
    const data = JSON.parse(user)
    const role = data?.professionRole
    if (!role) return null

    if (role === 'artist') return { track: 'artist', isTeamMember: false }

    if (TEAM_MEMBER_ROLES.includes(role)) {
      return { track: 'team-member', isTeamMember: true }
    }

    return null
  } catch {
    return null
  }
}

const onboardingKey = (track: TutorialTrack) => `dizee-onboarding-${track}`
const ONBOARDING_RESUME_KEY = 'dizee-onboarding-resume'

export default function OnboardingTutorial() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [track, setTrack] = useState<TutorialTrack | null>(null)
  const [isTeamMemberUser, setIsTeamMemberUser] = useState(false)
  const [sheetsCredentials, setSheetsCredentials] = useState<{
    workspaceId: string
    secretToken: string
    webhookUrl: string
  } | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [connectingGoogle, setConnectingGoogle] = useState(false)

  const steps = useMemo(() => {
    if (!track) return []
    return stepsByRole[track]
  }, [track])

  useEffect(() => {
    const info = getTrackFromStorage()
    if (!info) return
    setTrack(info.track)
    setIsTeamMemberUser(info.isTeamMember)

    let cancelled = false
    let timeoutId: number | null = null

    const clearTimer = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const decideShow = (serverCompleted: boolean | null, seenInStorage: boolean) => {
      if (cancelled) return
      clearTimer()
      if (serverCompleted === true) {
        localStorage.setItem(onboardingKey(info.track), new Date().toISOString())
        return
      }
      if (serverCompleted === false) {
        const resumeStepRaw = sessionStorage.getItem(ONBOARDING_RESUME_KEY)
        if (resumeStepRaw !== null) {
          sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
          const nextStep = Math.max(0, Math.min(parseInt(resumeStepRaw, 10), stepsByRole[info.track].length - 1))
          setStepIndex(nextStep)
          setIsOpen(true)
          return
        }
        timeoutId = window.setTimeout(() => {
          timeoutId = null
          if (!cancelled) {
            setStepIndex(0)
            setIsOpen(true)
          }
        }, 900)
        return
      }
      // Server check failed — fall back to localStorage
      if (seenInStorage) return
      const resumeStepRaw = sessionStorage.getItem(ONBOARDING_RESUME_KEY)
      if (resumeStepRaw !== null) {
        sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
        const nextStep = Math.max(0, Math.min(parseInt(resumeStepRaw, 10), stepsByRole[info.track].length - 1))
        setStepIndex(nextStep)
        setIsOpen(true)
        return
      }
      timeoutId = window.setTimeout(() => {
        timeoutId = null
        if (!cancelled) {
          setStepIndex(0)
          setIsOpen(true)
        }
      }, 900)
    }

    const seenInStorage = !!localStorage.getItem(onboardingKey(info.track))

    const typeParam = info.isTeamMember ? 'team' : 'artist'
    getDataAPI(`user/onboarding-completed?type=${typeParam}`)
      .then((res: any) => {
        const outer = res?.data ?? res
        const data = outer?.data ?? outer
        const completed = data?.completed === true
        decideShow(completed, seenInStorage)
      })
      .catch(() => {
        decideShow(null, seenInStorage)
      })

    return () => {
      cancelled = true
      clearTimer()
    }
  }, [])

  useEffect(() => {
    const loadSheetsCredentials = async () => {
      try {
        const response = await getDataAPI('integrations/google-sheets/settings')
        const data = response?.data?.data ?? response?.data ?? response
        
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://diz.ee'
        const defaultWebhookUrl = `${backendUrl.replace(/\/$/, '')}/api/v1/integrations/google-sheets/sync`
        
        setSheetsCredentials({
          workspaceId: data?.workspaceId || '',
          secretToken: data?.secretToken || '',
          webhookUrl: data?.webhookUrl || defaultWebhookUrl,
        })
      } catch (error) {
        console.error('Failed to load Google Sheets credentials:', error)
        const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://diz.ee'
        setSheetsCredentials({
          workspaceId: '',
          secretToken: '',
          webhookUrl: `${backendUrl.replace(/\/$/, '')}/api/v1/integrations/google-sheets/sync`,
        })
      }
    }

    if (isOpen) {
      loadSheetsCredentials()
    }
  }, [isOpen])

  const markComplete = () => {
    if (!track) return
    const iso = new Date().toISOString()
    localStorage.setItem(onboardingKey(track), iso)
    setIsOpen(false)
    const typeParam = isTeamMemberUser ? 'team' : 'artist'
    postDataAPI('user/onboarding-completed', { completed: true, completedAt: iso, type: typeParam }).catch(() => {})
  }

  const handleNext = () => {
    if (stepIndex >= steps.length - 1) {
      markComplete()
      return
    }
    setStepIndex((current) => current + 1)
  }

  const handleBack = () => {
    setStepIndex((current) => Math.max(0, current - 1))
  }

  const handleAction = async (actionType: string) => {
    switch (actionType) {
      case 'openGoogleCalendar': {
        setConnectingGoogle(true)
        try {
          sessionStorage.setItem('googleConnecting', '1')
          sessionStorage.setItem(ONBOARDING_RESUME_KEY, String(stepIndex + 1))
          const redirectUri = `${window.location.origin}/google-calendar-callback`
          const resp = await getDataAPI(
            `user/connectors/googleCalendar/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`
          )
          const authUrl =
            resp?.data?.data?.authUrl ?? resp?.data?.authUrl ?? resp?.authUrl ?? resp?.data?.data?.url ?? resp?.url
          if (!authUrl || typeof authUrl !== 'string') {
            sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
            window.alert('Failed to initiate Google connection.')
            return
          }
          window.location.href = authUrl
        } catch (e) {
          console.error('Failed to start Google connection:', e)
          sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
          window.alert('Failed to initiate Google connection. Please try again.')
        } finally {
          setConnectingGoogle(false)
        }
        return
      }
      case 'openGoogleSheets': {
        const sheetsTemplateId = import.meta.env.VITE_GOOGLE_SHEETS_TEMPLATE_ID || '1FoYR6rZzvxBXshWZXREB-1P1Mg9udLcGPFFJmzOACj8'
        window.open(`https://docs.google.com/spreadsheets/d/${sheetsTemplateId}/copy`, '_blank')
        markComplete()
        setTimeout(() => navigate('/settings'), 500)
        return
      }
      case 'openShows':
        markComplete()
        navigate('/shows')
        return
      case 'openMusic':
        markComplete()
        navigate('/music')
        return
      case 'openTeam':
        markComplete()
        navigate('/team')
        return
      case 'openSettings':
        markComplete()
        navigate('/settings')
        return
    }
  }

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 1500)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  if (!track || steps.length === 0) return null

  const currentStep = steps[stepIndex]

  return (
    <Modal
      isOpen={isOpen}
      onClose={markComplete}
      title={stepIndex === 0 ? currentStep.title : 'Getting Started'}
      description={stepIndex === 0 ? currentStep.description : undefined}
      maxWidthClassName="max-w-xl"
    >
      <div className="space-y-6">
        {/* Header with duration badge */}
        {stepIndex === 0 ? (
          <div className="text-center">
            <span className="inline-block text-xs font-medium text-white dark:text-gray-900 bg-gray-900 dark:bg-white px-3 py-1.5 rounded-full shadow-sm">
              Setup time: {currentStep.duration}
            </span>
          </div>
        ) : (
          <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{currentStep.title}</h3>
              <span className="flex-shrink-0 text-xs font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-1.5 rounded-full">
                {currentStep.duration}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">{currentStep.description}</p>
          </div>
        )}

        {/* Google Sheets Credentials Section */}
        {currentStep.showGoogleSheetsCredentials && sheetsCredentials ? (
          <div className="space-y-4 bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Your Credentials
            </p>
            
            <div className="space-y-3">
              {/* Workspace ID */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Workspace ID</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={sheetsCredentials.workspaceId || 'Loading...'}
                    className="flex-1 px-3 py-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopy(sheetsCredentials.workspaceId, 'workspaceId')}
                    disabled={!sheetsCredentials.workspaceId}
                    className="px-4 py-2.5 text-xs font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {copiedField === 'workspaceId' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Secret Token */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Secret Token</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={sheetsCredentials.secretToken || 'Loading...'}
                    className="flex-1 px-3 py-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopy(sheetsCredentials.secretToken, 'secretToken')}
                    disabled={!sheetsCredentials.secretToken}
                    className="px-4 py-2.5 text-xs font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {copiedField === 'secretToken' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Webhook URL */}
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Webhook URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={sheetsCredentials.webhookUrl}
                    className="flex-1 px-3 py-2.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white font-mono focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => handleCopy(sheetsCredentials.webhookUrl, 'webhookUrl')}
                    className="px-4 py-2.5 text-xs font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all"
                  >
                    {copiedField === 'webhookUrl' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Content Cards */}
        {currentStep.items && currentStep.items.length > 0 ? (
          <div className="space-y-3">
            {currentStep.items.map((item, index) => (
              <div 
                key={item.title} 
                className="relative overflow-hidden p-5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
              >
                <div className="flex gap-4">
                  {currentStep.showGoogleSheetsCredentials ? null : (
                    <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-gray-900 dark:bg-white flex items-center justify-center text-white dark:text-gray-900 font-semibold text-xs">
                      {index + 1}
                    </div>
                  )}
                  <div className="flex-1 space-y-1.5">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
                      {item.title}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {/* Action Buttons */}
        {currentStep.actions ? (
          <div className="flex flex-col gap-3 pt-2">
            {currentStep.actions.map((action) => {
              const isGoogleCalendar = action.onClick === 'openGoogleCalendar'
              const isLoading = isGoogleCalendar && connectingGoogle
              return (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => handleAction(action.onClick)}
                  disabled={isLoading}
                  className={`w-full text-sm font-medium py-3.5 rounded-xl transition-all shadow-sm disabled:opacity-70 disabled:cursor-wait ${
                    action.type === 'primary'
                      ? 'bg-gray-900 text-white hover:bg-black border border-transparent dark:bg-white/10 dark:text-white dark:border-white/15 dark:hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 dark:focus-visible:ring-white/25'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {isLoading ? 'Connecting…' : action.label}
                </button>
              )
            })}
          </div>
        ) : null}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
            Step {stepIndex + 1} of {steps.length}
          </span>
          <button 
            type="button" 
            onClick={markComplete} 
            className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium transition-colors"
          >
            Skip tutorial
          </button>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col gap-2.5">
          <button 
            type="button" 
            onClick={handleNext} 
            className="w-full bg-gray-900 text-white text-sm font-medium py-3.5 rounded-xl hover:bg-black transition-all shadow-sm border border-transparent dark:bg-white/10 dark:text-white dark:border-white/15 dark:hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 dark:focus-visible:ring-white/25"
          >
            {stepIndex >= steps.length - 1 ? 'Complete Setup' : 'Continue'}
          </button>
          {stepIndex > 0 ? (
            <button
              type="button"
              onClick={handleBack}
              className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-medium py-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
            >
              Back
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}
